import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getMarketStatus } from '../utils/marketStatus';
import { formatLocalTime } from '../utils/formatters';

// Polling interval for live data (30 seconds) - Requirements 7.1, 7.2
const LIVE_POLLING_INTERVAL = 30000;

// Maximum number of probability history points to keep
const MAX_PROB_HISTORY_POINTS = 100;

// Transform single market data
function transformMarket(m) {
  if (!m) return null;
  
  // Normalize clobTokenIds from API (can be array or JSON string)
  let clobTokenIds = m.clobTokenIds;
  if (typeof clobTokenIds === 'string') {
    try {
      const parsed = JSON.parse(clobTokenIds);
      clobTokenIds = Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      clobTokenIds = [clobTokenIds];
    }
  }

  // Extract token IDs from clobTokenIds array
  // clobTokenIds[0] = YES token, clobTokenIds[1] = NO token
  const tokenIdA = clobTokenIds?.[0] || null;
  const tokenIdB = clobTokenIds?.[1] || null;
  
  return {
    id: m.marketId,
    conditionId: m.conditionId,
    slug: m.marketSlug,
    title: m.title,
    teamA: m.teamA?.name || m.teamA,
    teamB: m.teamB?.name || m.teamB,
    priceA: m.prices?.yes || 0,
    priceB: m.prices?.no || 0,
    polyYes: m.prices?.yes,
    polyNo: m.prices?.no,
    volume: m.volume || 0,
    liquidity: m.liquidity || 0,
    status: getMarketStatus(m),
    gameTime: m.startTime,
    sport: m.sport,
    polymarketUrl: `https://polymarket.com/event/${m.eventSlug}`,
    // Token IDs for trading
    tokenIdA,
    tokenIdB,
  };
}

export function useMarketDetail(marketId) {
  const { loading: authLoading } = useAuth();
  const [market, setMarket] = useState(null);
  const [holders, setHolders] = useState(null);
  const [smartMoneyPositions, setSmartMoneyPositions] = useState(null);
  const [oddsHistory, setOddsHistory] = useState(null);
  const [oddsCompare, setOddsCompare] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [playerProps, setPlayerProps] = useState(null);
  const [playerPropsLoading, setPlayerPropsLoading] = useState(false);
  const [playerPropsError, setPlayerPropsError] = useState(null);
  const [playerPropsFetched, setPlayerPropsFetched] = useState(false);
  const [playerPropsAccess, setPlayerPropsAccess] = useState({
    isAuthRequired: false,
    isMembershipRequired: false,
  });
  const [gameDetail, setGameDetail] = useState(null);
  const [gameId, setGameId] = useState(null);
  const [teamStats, setTeamStats] = useState({ teamA: null, teamB: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessStatus, setAccessStatus] = useState({
    isAuthRequired: false,
    isMembershipRequired: false,
    currentTier: 'free',
    pointsUnlocked: false,
  });

  // Live game states (Requirements 7.1, 7.2)
  const [liveScore, setLiveScore] = useState(null);
  const [lineups, setLineups] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState(null);
  // Probability history for win probability chart (collected in real-time)
  const [probHistory, setProbHistory] = useState([]);
  
  // Ref to track if component is mounted for cleanup
  const isMountedRef = useRef(true);
  // Ref to store polling interval ID
  const pollingIntervalRef = useRef(null);

  // Fetch live data when market status is 'live' (Requirements 7.1, 7.2)
  const fetchLiveData = useCallback(async (marketData, currentGameId) => {
    if (!marketData || marketData.status !== 'live') return;
    if (!currentGameId) return; // Need gameId to fetch live data
    
    try {
      setLiveLoading(true);
      
      // Fetch live score and lineups in parallel (Free tier - no auth required)
      // Use currentGameId (balldontlie gameId) for live-score API
      const [liveScoreData, lineupsData] = await Promise.all([
        api.getLiveScore(currentGameId).catch(() => null),
        api.getLineups(currentGameId).catch(() => null),
      ]);
      
      if (isMountedRef.current) {
        if (liveScoreData) {
          setLiveScore(liveScoreData);
          
          // Collect probability history from Polymarket odds
          // Use market prices as win probability (priceA = away team, priceB = home team)
          const now = new Date();
          const timeLabel = formatLocalTime(now, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          });
          
          // Calculate win probability based on current score and period
          // For now, use Polymarket odds as proxy for win probability
          const homeProb = marketData.priceB || 0.5;
          const awayProb = marketData.priceA || 0.5;
          
          setProbHistory(prev => {
            const newPoint = {
              time: timeLabel,
              homeProb: homeProb,
              awayProb: awayProb,
              period: liveScoreData.period,
              clock: liveScoreData.clock,
            };
            
            // Avoid duplicate entries for the same time
            const lastPoint = prev[prev.length - 1];
            if (lastPoint && lastPoint.time === timeLabel) {
              return prev;
            }
            
            // Keep only the last MAX_PROB_HISTORY_POINTS points
            const updated = [...prev, newPoint];
            if (updated.length > MAX_PROB_HISTORY_POINTS) {
              return updated.slice(-MAX_PROB_HISTORY_POINTS);
            }
            return updated;
          });
        }
        if (lineupsData) {
          setLineups(lineupsData);
        }
        setLiveError(null);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setLiveError(err.message || 'Failed to fetch live data');
      }
    } finally {
      if (isMountedRef.current) {
        setLiveLoading(false);
      }
    }
  }, []);

  // Setup polling for live data (Requirement 7.2)
  const setupLivePolling = useCallback((marketData, currentGameId) => {
    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    // Only setup polling if market is live
    if (!marketData || marketData.status !== 'live') return;
    
    // Initial fetch
    fetchLiveData(marketData, currentGameId);
    
    // Setup polling every 30 seconds
    pollingIntervalRef.current = setInterval(() => {
      fetchLiveData(marketData, currentGameId);
    }, LIVE_POLLING_INTERVAL);
  }, [fetchLiveData]);

  const fetchDetail = useCallback(async () => {
    if (!marketId) return;
    try {
      setLoading(true);
      const aggregate = await api.getMarketDetailAggregate(marketId, 24);
      const payload = aggregate?.data || {};
      let marketData = transformMarket(payload.market);

      if (!marketData) {
        setError(new Error('Market not found'));
        return;
      }

      // Inject token IDs from aggregation payload when available
      if (!marketData.tokenIdA && payload.tokens) {
        marketData = {
          ...marketData,
          tokenIdA: payload.tokens.token_id_a || null,
          tokenIdB: payload.tokens.token_id_b || null,
        };
      }

      // Fallback: fetch token IDs by slug if aggregation didn't include them
      if (!marketData.tokenIdA && marketData.slug) {
        try {
          const tokenRes = await api.getMarketTokensBySlug(marketData.slug);
          if (tokenRes.code === 'SUCCESS' && tokenRes.data) {
            marketData = {
              ...marketData,
              tokenIdA: tokenRes.data.token_id_a,
              tokenIdB: tokenRes.data.token_id_b,
            };
          }
        } catch (e) {
          console.warn('Failed to fetch token IDs:', e);
        }
      }

      setMarket(marketData);

      setHolders(payload.holders || null);
      setSmartMoneyPositions(payload.smart_money_positions || null);
      setOddsHistory(payload.odds_history || null);
      setOddsCompare(payload.odds_compare || null);
      
      // Handle prediction response - API returns { marketId: predictionData }
      let predictionResult = null;
      const predData = payload.predictions;
      if (predData) {
        // Check if it's the batch response format { "marketId": { prob, confidence, ... } }
        if (predData[marketId]) {
          predictionResult = predData[marketId];
        } else if (Array.isArray(predData)) {
          predictionResult = predData[0];
        } else if (predData.predictions) {
          predictionResult = predData.predictions[0];
        }
      }
      setPrediction(predictionResult);

      const pointsUnlocked = !!aggregate?.access?.points_unlocked;
      const accessFlags = {
        isAuthRequired: !!aggregate?.access?.auth_required,
        isMembershipRequired: !!aggregate?.access?.membership_required && !pointsUnlocked,
        currentTier: aggregate?.access?.current_tier || 'free',
        pointsUnlocked,
      };
      setAccessStatus(accessFlags);
      if (accessFlags.isMembershipRequired && accessFlags.currentTier !== 'free') {
        window.dispatchEvent(
          new CustomEvent('membership-required', {
            detail: { currentTier: accessFlags.currentTier },
          })
        );
      }

      setTeamStats(payload.team_stats || { teamA: null, teamB: null });
      const fetchedGameId = payload.game?.game_id || null;
      setGameId(fetchedGameId);
      setGameDetail(payload.game?.detail || null);

      if (marketData.status === 'live' && !fetchedGameId && marketData.teamA && marketData.teamB && marketData.gameTime) {
        marketData = { ...marketData, status: 'upcoming' };
        setMarket(marketData);
      }
      
      // Setup live data polling if market is live (Requirements 7.1, 7.2)
      if (marketData.status === 'live') {
        setupLivePolling(marketData, fetchedGameId);
      }
      
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [marketId, setupLivePolling]);

  const loadPlayerProps = useCallback(
    async ({ force = false } = {}) => {
      if (!gameId || !market?.teamA || !market?.teamB) return;
      if (!force && (playerPropsLoading || playerPropsFetched)) return;

      setPlayerPropsLoading(true);
      setPlayerPropsError(null);
      setPlayerPropsAccess({ isAuthRequired: false, isMembershipRequired: false });

      try {
        const result = await api.getPlayerProps(
          gameId,
          market.teamA,
          market.teamB,
          market.sport || 'nba',
          market?.id || marketId
        );

        if (result?.success && Array.isArray(result.props)) {
          setPlayerProps(result.props);
        } else {
          setPlayerProps([]);
          if (result?.error) {
            setPlayerPropsError(new Error(result.error));
          }
        }
      } catch (err) {
        setPlayerProps(null);
        if (err?.isAuthRequired) {
          setPlayerPropsAccess({ isAuthRequired: true, isMembershipRequired: false });
        } else if (err?.isMembershipRequired) {
          setPlayerPropsAccess({ isAuthRequired: false, isMembershipRequired: true });
        }
        setPlayerPropsError(err);
      } finally {
        setPlayerPropsLoading(false);
        setPlayerPropsFetched(true);
      }
    },
    [gameId, market?.teamA, market?.teamB, market?.sport, playerPropsFetched, playerPropsLoading]
  );

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      // Clear polling interval on unmount
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setPlayerProps(null);
    setPlayerPropsLoading(false);
    setPlayerPropsError(null);
    setPlayerPropsFetched(false);
    setPlayerPropsAccess({ isAuthRequired: false, isMembershipRequired: false });
  }, [marketId]);

  useEffect(() => {
    // Wait for auth to finish loading before fetching data
    if (!authLoading) {
      fetchDetail();
    }
  }, [fetchDetail, authLoading]);

  // Computed property for isLive status (Requirement 7.1)
  const isLive = market?.status === 'live';

  return {
    market,
    holders,
    smartMoneyPositions,
    oddsHistory,
    oddsCompare,
    prediction,
    playerProps,
    playerPropsLoading,
    playerPropsError,
    playerPropsAccess,
    loadPlayerProps,
    gameDetail,
    gameId,
    teamStats,
    accessStatus,
    loading,
    error,
    refetch: fetchDetail,
    // Live game data (Requirements 7.1, 7.2)
    isLive,
    liveScore,
    lineups,
    liveLoading,
    liveError,
    // Probability history for win probability chart
    probHistory,
  };
}
